import type { ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import type { PortalSignableDocument } from "@dearbill/shared";
import { fmtLongDate } from "@dearbill/shared";
import { plusOneMonth } from "./dashboardFormat";
import { CalendarCheckIcon, ShieldCheckIcon } from "./icons";
import { ActionNeededPanel, type PendingAction } from "./home/ActionNeededPanel";
import { documentActionCopy, documentsNeedingAction } from "./home/documentActions";
import { DocumentsCard } from "./home/DocumentsCard";
import { MailCard } from "./home/MailCard";
import { MedicareConnectionCard } from "./home/MedicareConnectionCard";
import { MedicationsCard } from "./home/MedicationsCard";
import { ProgramsCard } from "./home/ProgramsCard";
import { SavingsSummary } from "./home/SavingsSummary";
import { TimelineCard } from "./home/TimelineCard";
import type { DashboardOutletContext } from "./MemberDashboard";

/**
 * The Action Needed rows: the M3P form first, then each document this session
 * can sign (`documentsNeedingAction` order). Only the first row is primary.
 */
function pendingActions(
  onSign: (() => void) | undefined,
  documents: PortalSignableDocument[],
  onSignDocument: (doc: PortalSignableDocument) => void,
): PendingAction[] {
  const actions: PendingAction[] = [];
  if (onSign) {
    actions.push({
      key: "m3p",
      title: "Sign your Medicare Prescription Payment Plan (M3P) form",
      description:
        "Your form is ready. Review and sign it so we can submit your enrollment to your plan.",
      label: "Review and sign",
      onAction: onSign,
    });
  }
  for (const doc of documentsNeedingAction(documents)) {
    actions.push({
      key: doc.documentId,
      ...documentActionCopy(doc),
      onAction: () => onSignDocument(doc),
    });
  }
  return actions;
}

/**
 * The member Home content, rendered inside `MemberLayout` from the dashboard
 * the outlet context carries. The greeting is the page's `<h1>`; sections and
 * cards carry `<h2>` titles.
 */
export function HomeScreen(): ReactNode {
  const {
    data,
    years,
    planYear,
    setPlanYear,
    m3p,
    onSign,
    documents,
    onSignDocument,
    session,
    onSignOut,
  } = useOutletContext<DashboardOutletContext>();
  const name = [data.firstName, data.lastName]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");
  const nextReview = plusOneMonth(data.savingsLastUpdatedAt);
  const enrollment = data.latestEnrollment;
  const actions = pendingActions(onSign, documents, onSignDocument);
  return (
    <>
      <div className="flex flex-col gap-1">
        <h1 className="text-h2 text-midnight font-serif">
          {name ? `Welcome back, ${name}` : "Welcome back"}
        </h1>
        {data.savingsLastUpdatedAt !== null && (
          <p className="text-body-sm text-slate-teal">
            Last updated on {fmtLongDate(data.savingsLastUpdatedAt)}
          </p>
        )}
      </div>

      <div className="border-success-border bg-success-bg flex items-center gap-5 rounded-2xl border p-6">
        <ShieldCheckIcon size={28} className="text-success shrink-0" />
        <p className="text-body text-forest font-medium">
          {
            "You’re improving your healthcare affordability. We'll continue guiding and supporting you along the way."
          }
        </p>
      </div>

      {actions.length > 0 && <ActionNeededPanel actions={actions} />}

      {documents.length > 0 && <DocumentsCard documents={documents} role={data.role} />}

      <SavingsSummary m3p={m3p} years={years} planYear={planYear} onPlanYearChange={setPlanYear} />

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <ProgramsCard enrollment={enrollment} />
        <MedicationsCard prescriptions={enrollment?.prescriptionsList ?? []} />
      </div>

      <MedicareConnectionCard connection={data.medicareConnection} />

      <TimelineCard session={session} onSignOut={onSignOut} />

      <MailCard session={session} onSignOut={onSignOut} />

      <div className="border-haze bg-ice flex items-center gap-5 rounded-2xl border p-6">
        <CalendarCheckIcon size={24} className="text-slate-teal shrink-0" />
        <div className="flex flex-col gap-1">
          <p className="text-body text-midnight font-bold">
            {nextReview === null
              ? "DearBill rechecks your eligibility every month"
              : `Your next monthly eligibility review is on ${fmtLongDate(nextReview)}`}
          </p>
          <p className="text-body-sm text-slate-teal">
            DearBill automatically rechecks all official programs every month. You do not need to
            resubmit your medications.
          </p>
        </div>
      </div>
    </>
  );
}
